/**
 * Browser push notifications via OneSignal (react-onesignal npm).
 * OneSignal is initialized in AppFeature.tsx when meta name="onesignalAppId" is present.
 *
 * NOTE: We use dynamic import() instead of a static top-level import to prevent
 * react-onesignal from injecting cdn.onesignal.com script at module evaluation time.
 * Chrome Extension CSP blocks external scripts, so the static import was causing
 * violations even when OneSignal was never used inside the extension.
 */

function isOneSignalEnabled(): boolean {
  if (typeof document === "undefined") return false;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="onesignalAppId"]');
  return !!meta?.getAttribute("content");
}

async function getOneSignal() {
  // Never load OneSignal inside a Chrome Extension — CSP blocks external scripts.
  if (typeof chrome !== "undefined" && chrome.runtime?.id) return null;
  // const mod = await import("react-onesignal");
  // return mod.default;
  return null;
}

export async function isPushSupported(): Promise<boolean> {
  if (!isOneSignalEnabled()) return false;
  try {
    const OneSignal = await getOneSignal();
    if (!OneSignal) return false;
    return OneSignal.Notifications?.isPushSupported?.() ?? false;
  } catch {
    return false;
  }
}

export async function getNotificationPermission(): Promise<NotificationPermission> {
  if (!isOneSignalEnabled()) return "denied";
  try {
    const OneSignal = await getOneSignal();
    if (!OneSignal) return "denied";
    const native = OneSignal.Notifications?.permissionNative;
    if (native === "granted") return "granted";
    if (native === "denied") return "denied";
    return "default";
  } catch {
    return "denied";
  }
}

/** True when the last permission request failed because the user previously dismissed the prompt. */
let pushPreviouslyDismissed = false;

export function wasPushPreviouslyDismissed(): boolean {
  return pushPreviouslyDismissed;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isOneSignalEnabled()) return "denied";
  pushPreviouslyDismissed = false;
  try {
    const OneSignal = await getOneSignal();
    if (!OneSignal) return "denied";
    await OneSignal.Notifications.requestPermission();
    return (await getNotificationPermission()) as NotificationPermission;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/previously dismissed|dismissed/i.test(msg)) {
      pushPreviouslyDismissed = true;
    }
    return "denied";
  }
}

export async function enableBrowserNotifications(userId: string): Promise<boolean> {
  if (!(await isPushSupported())) return false;
  try {
    const perm = await requestNotificationPermission();
    if (perm !== "granted") return false;
    await setOneSignalUserId(userId);
    return true;
  } catch {
    return false;
  }
}

/** Associate the current browser with a user for push targeting (call when user is known). */
export async function setOneSignalUserId(userId: string): Promise<void> {
  if (!isOneSignalEnabled() || !userId) return;
  try {
    const OneSignal = await getOneSignal();
    if (!OneSignal) return;
    await OneSignal.login(userId);
  } catch {
    // ignore
  }
}
